import Button from 'Components/Base/Button'
import Icon from 'Components/Base/Icon'
import Row from 'Components/Base/Row'
import Typography from 'Components/Base/Typography'
import { useFormik } from 'formik'
import { useAppSelector } from 'hooks/hooks'
import useNewEnrollment from 'hooks/useNewEnrollment'
import React from 'react'
import { Form } from 'react-bootstrap'
import * as ReactDOMServer from 'react-dom/server'
import ReactTooltip from 'react-tooltip'
import { getAccessToken } from 'reducers/authSlice'
import { getProfile } from 'reducers/profileSlice'
import styled, { useTheme } from 'styled-components'
import formatPhoneNumberInput from 'utilities/formatPhoneNumberInput'
import * as Yup from 'yup'

interface props {
  ctl: boolean
  handleSubmit: () => void
}

export default function Phone({ ctl, handleSubmit }: props) {
  const theme = useTheme()
  const authToken = useAppSelector(getAccessToken)
  const profile = useAppSelector(getProfile)
  const { postPhone } = useNewEnrollment({ token: authToken })
  const initialValues = {
    phone:
      profile?.primaryComm === 'MP'
        ? formatPhoneNumberInput(profile?.cellPhone) ?? ''
        : profile?.primaryComm === 'HP'
        ? formatPhoneNumberInput(profile.homePhone) ?? ''
        : profile?.cellPhone !== null
        ? formatPhoneNumberInput(profile?.cellPhone) ?? ''
        : '',
    optIn: true,
    deviceType: profile?.primaryComm === 'HP' ? 2 : 1,
  }

  const validationSchema = Yup.object().shape({
    phone: Yup.string()
      .min(14, 'Phone must be 10 digits')
      .required('Phone is required')
      .matches(/^\(\d{3}\) \d{3}-\d{4}$/, 'Phone is invalid'),
  })

  const onSubmit = (contactInfo: { phone: string; optIn: boolean; deviceType: number }) => {
    const payload = {
      ...contactInfo,
      phone: contactInfo.phone?.replace(/[^\d]/g, ''),
    }
    postPhone(payload).then(() => {
      handleSubmit()
    })
  }

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
  })

  return (
    <Container>
      <Header>Almost Done!</Header>
      <Header1>Just One More Step</Header1>
      <SubHeader>Thanks for picking a time to speak with your Tria Health {ctl ? 'coach' : 'pharmacist'}!</SubHeader>
      <SubHeader>
        We currently have the following phone number listed for you. If you'd like us to use a different number, please update it below.
      </SubHeader>
      <StyledPhonePair>
        <PhoneInputWrap>
          <StyledPhoneInput
            name="phone"
            isInvalid={!!formik.touched.phone && !!formik.errors.phone}
            onChange={(value: React.ChangeEvent<HTMLInputElement>) =>
              formik.setFieldValue('phone', formatPhoneNumberInput(value.currentTarget.value))
            }
            onBlur={formik.handleBlur}
            value={formik.values.phone}
            placeholder="Phone Number ((###) ###-####)*"
          />
          {formik.touched.phone && formik.errors.phone && <ErrorText>{formik.errors.phone}</ErrorText>}
        </PhoneInputWrap>
        <div
          data-for="discontinue"
          data-html="true"
          data-tip={ReactDOMServer.renderToStaticMarkup(
            <div style={{ textAlign: 'left' }}>
              <Typography size={13}>This phone number will only be</Typography>
              <Typography size={13}>used by us to contact you for </Typography>
              <Typography size={13}>appointments, text reminders</Typography>
              <Typography size={13}>and other Tria information.</Typography>
            </div>,
          )}
        >
          <div>
            <Icon name="info-circle-solid" size="1x" styles={{ marginLeft: -33, marginTop: 8 }}></Icon>
          </div>
        </div>
        <ReactTooltip id="discontinue" place="top" textColor="#000000" backgroundColor="#FFFFFF" border />
        <StyledPhoneDropdown
          value={formik.values.deviceType}
          as="select"
          onChange={(value: React.ChangeEvent<HTMLSelectElement>) => formik.setFieldValue('deviceType', value.currentTarget.value)}
          style={{ color: 'black' }}
        >
          <option value={1} style={{ color: 'black' }}>
            Mobile
          </option>
          <option value={2} style={{ color: 'black' }}>
            Home
          </option>
        </StyledPhoneDropdown>
      </StyledPhonePair>
      <Row styles={{ marginTop: 25, maxWidth: 500, margin: '10px auto 50px' }}>
        <Checkbox type="checkbox" checked={formik.values.optIn} onChange={() => formik.setFieldValue('optIn', !formik.values.optIn)}></Checkbox>
        <Typography styles={{ textAlign: 'left' }}>
          <b>Tria Health Text Alerts:</b> Opt-in for appointment reminders, medication savings, and more!
        </Typography>
      </Row>
      <Button text="Submit" styles={{ marginBottom: 30, backgroundColor: theme.colors.status.good }} width={150} onClick={formik.handleSubmit} />
    </Container>
  )
}

const Container = styled.div`
  font-family: Raleway;
  text-align: center;
  max-width: 1200px;
  margin: 80px auto;
  padding: 80px 60px;
  border-radius: 34px;
  border: 1px solid #bdbdbd;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
  justify-content: center;

  @media only screen and (max-width: 600px) {
    padding: 30px 20px;
  }
`
const Header = styled.h1`
  font-weight: 700;
  font-size: 40px;
  margin: 20px auto 0px;
`
const Header1 = styled.h2`
  font-weight: 700;
  font-size: 40px;
  margin: 0px auto 30px;
`

const SubHeader = styled.h2`
  font-weight: 200;
  font-size: 20px;
  margin-left: auto;
  margin-right: auto;
  max-width: 640px;
  margin: 20px auto;
`
const StyledInput = styled(Form.Control)`
  border-radius: 3px;
`

const StyledPhoneDropdown = styled(StyledInput)`
  text-align: center;
  min-height: 50;
  border: 1px solid #98a4ae;
  background-color: #ffffff;
  min-height: 50px;
  margin-left: 20px;
  margin-bottom: 10px;
  min-width: 20%;
`

const StyledPhoneInput = styled(Form.Control)`
  background-color: #edfdff;
  text-align: center;
  border-radius: 4px;
  border: 1px solid transparent;
  box-shadow: inset 0px 1px 3px;
  min-height: 50px;
  margin-bottom: 10px;
  width: 100%;

  &.is-invalid {
    border-color: #d32f2f;
    box-shadow: inset 0px 1px 3px, 0 0 0 1px #d32f2f;
  }
`
const StyledPhonePair = styled.div`
  flex-direction: row;
  display: flex;
  max-width: 500px;
  margin: 20px auto;
`
const PhoneInputWrap = styled.div`
  width: 80%;
`
const ErrorText = styled.div`
  color: #d32f2f;
  font-size: 13px;
  text-align: left;
  margin-top: -6px;
  margin-bottom: 8px;
`
const Checkbox = styled.input`
  width: 25px;
  height: 25px;
  accent-color: #00677f;
  margin: 0px 8px auto 5px;

  /* Optional: make it even larger visually */
`
